import { useState } from "react";
import { Send, ShieldCheck, Heart } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { IMG } from "../lib/images";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/motion";

export default function Prayer() {
  const [form, setForm] = useState({ first_name: "", contact: "", subject: "", wants_callback: false, confidential: false, website: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/prayer-requests", form);
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        testid="prayer-header"
        eyebrow="Prière"
        titleLines={["Nous pouvons", "prier pour vous"]}
        intro="Vous traversez une situation difficile ? Vous avez besoin de direction, de guérison, de paix ou souhaitez simplement confier une situation à Dieu ? Vous pouvez nous transmettre votre sujet de prière."
        image={IMG.priere}
      />
      <section className="py-16 sm:py-24">
        <div className="container-site grid items-start gap-12 lg:grid-cols-2">
          <div>
            <Reveal>
              <blockquote className="border-l-2 border-terracotta pl-6">
                <p className="verse-quote">
                  « Ne vous inquiétez de rien ; mais en toute chose faites connaître vos besoins à Dieu
                  par des prières et des supplications, avec des actions de grâces. »
                </p>
                <cite className="mt-2 block font-mono text-xs uppercase not-italic tracking-[0.2em] text-stone-500">Philippiens 4:6</cite>
              </blockquote>
              <div className="mt-10 space-y-5">
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-olive-soft text-olive-deep">
                    <ShieldCheck className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-serif text-lg font-semibold text-obsidian">Respect et confidentialité</h3>
                    <p className="mt-1 text-sm leading-relaxed text-stone-600">
                      Chaque demande est lue avec soin et traitée avec respect. Les demandes marquées
                      « confidentielles » ne sont partagées qu'avec la personne qui prie pour vous.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-terracotta-soft text-terracotta">
                    <Heart className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-serif text-lg font-semibold text-obsidian">Une équipe qui prie vraiment</h3>
                    <p className="mt-1 text-sm leading-relaxed text-stone-600">
                      Vos sujets sont portés dans nos temps de prière et d'intercession. Vous n'êtes pas seul.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            {sent ? (
              <div data-testid="prayer-form-success" className="card-warm p-10 text-center">
                <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-terracotta-soft text-terracotta">
                  <Heart className="h-6 w-6" />
                </span>
                <p className="mt-6 font-serif text-2xl font-medium text-obsidian">Votre demande est entre de bonnes mains</p>
                <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-stone-600">
                  Merci pour votre confiance. Nous allons prier pour vous, avec respect et
                  confidentialité. Que la paix de Dieu vous accompagne.
                </p>
                <button data-testid="prayer-form-again" onClick={() => { setSent(false); setForm({ first_name: "", contact: "", subject: "", wants_callback: false, confidential: false, website: "" }); }}
                  className="btn-outline mt-8">
                  Déposer un autre sujet
                </button>
              </div>
            ) : (
              <form data-testid="prayer-form" onSubmit={submit} className="card-warm space-y-4 p-7 sm:p-9">
                <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <div>
                  <label htmlFor="p-firstname" className="mb-1.5 block text-sm font-semibold text-obsidian">Prénom *</label>
                  <input id="p-firstname" data-testid="prayer-firstname-input" required className="input-field" value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Votre prénom" />
                </div>
                <div>
                  <label htmlFor="p-contact" className="mb-1.5 block text-sm font-semibold text-obsidian">Email ou téléphone (facultatif)</label>
                  <input id="p-contact" data-testid="prayer-contact-input" className="input-field" value={form.contact}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Si vous souhaitez être recontacté" />
                </div>
                <div>
                  <label htmlFor="p-subject" className="mb-1.5 block text-sm font-semibold text-obsidian">Votre sujet de prière *</label>
                  <textarea id="p-subject" data-testid="prayer-subject-input" required rows={5} className="input-field resize-none" value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Confiez-nous ce qui vous tient à cœur…" />
                </div>
                <label className="flex cursor-pointer items-start gap-3 text-sm text-stone-600">
                  <input data-testid="prayer-callback-checkbox" type="checkbox" checked={form.wants_callback}
                    onChange={(e) => setForm({ ...form, wants_callback: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-line text-terracotta accent-terracotta" />
                  Je souhaite être recontacté
                </label>
                <label className="flex cursor-pointer items-start gap-3 text-sm text-stone-600">
                  <input data-testid="prayer-confidential-checkbox" type="checkbox" checked={form.confidential}
                    onChange={(e) => setForm({ ...form, confidential: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-line text-terracotta accent-terracotta" />
                  Je souhaite que cette demande reste confidentielle
                </label>
                <button data-testid="prayer-submit-btn" type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
                  <Send className="h-4 w-4" />
                  {loading ? "Envoi…" : "Envoyer ma demande de prière"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
